<script lang="ts">
  // Ported + simplified from fe/src/lib/components/ChatMessage.svelte.
  // Guests cannot react, forward, edit, or delete (§9 open items), so the
  // support-widget variant drops all of those menus and renders only the
  // core bubble + timestamp.
  import type { Message } from '../types';
  import Avatar from './Avatar.svelte';
  import { t } from '../i18n/index';

  let {
    message,
    visitorId,
    visitorName,
    greeting = false,
  }: {
    message: Message;
    visitorId: string;
    visitorName: string;
    greeting?: boolean;
  } = $props();

  const isSelfMessage = $derived(
    message.source === 'Guest' && message.created_by === visitorId
  );
  const isSystemMessage = $derived(
    message.type === 'system' || message.source === 'System'
  );
  const isAiMessage = $derived(
    ['AI', 'Bot'].includes(message.source) || greeting
  );

  function getBubbleClasses(): string {
    if (isSystemMessage) {
      return 'tashimoto-support-bg-yellow-100 tashimoto-support-text-gray-800 tashimoto-support-text-center';
    }
    if (isAiMessage) {
      return 'tashimoto-support-bg-purple-50 tashimoto-support-text-gray-800 tashimoto-support-rounded-bl-none tashimoto-support-border tashimoto-support-border-purple-200';
    }
    if (isSelfMessage) {
      return 'tashimoto-support-bg-blue-500 tashimoto-support-text-white tashimoto-support-rounded-br-none';
    }
    return 'tashimoto-support-bg-white tashimoto-support-text-gray-800 tashimoto-support-rounded-bl-none tashimoto-support-border tashimoto-support-border-gray-200';
  }

  const senderName = $derived.by(() => {
    if (isAiMessage) return t('chat.aiAgent');
    if (isSystemMessage) return t('chat.system');
    if (isSelfMessage) return visitorName || 'You';
    return message.source === 'Agent' ? 'Agent' : t('chat.aiAgent');
  });

  // URL autolinking. Mirrors fe/src/App.svelte's `splitUrlsInChunks`: plain
  // text chunks are split on http(s):// / bare www. runs so they can render as
  // real anchors instead of inert text.
  const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

  type TextPart = { type: string; text: string };

  function splitUrls(text: string): TextPart[] {
    const parts: TextPart[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    URL_PATTERN.lastIndex = 0;
    while ((match = URL_PATTERN.exec(text))) {
      if (match.index > lastIndex) {
        parts.push({ type: 'content', text: text.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'link', text: match[0] });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push({ type: 'content', text: text.slice(lastIndex) });
    }
    return parts;
  }

  // A bare `www.x` in an href resolves against the host page as a relative
  // path, so give it a scheme.
  function hrefFor(text: string): string {
    return /^https?:\/\//i.test(text) ? text : `https://${text}`;
  }

  function renderParts(m: string | TextPart[]): TextPart[] {
    const chunks: TextPart[] = Array.isArray(m)
      ? m
      : [{ type: 'content', text: m ?? '' }];
    // Server-supplied `link` chunks are already links; only split plain text.
    return chunks.flatMap((chunk) =>
      chunk.type === 'link' ? [chunk] : splitUrls(chunk.text ?? '')
    );
  }

  const isFileMessage = $derived(message.type === 'file' && !!message.file_info);
  const isImageFile = $derived(
    isFileMessage && /^image\//.test(message.file_info?.content_type || '')
  );

  function humanSize(bytes: number | undefined): string {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // Visitor-facing display text for system messages. Some server-side system
  // messages carry agent-id placeholders (`@{<agent_id>}`) intended for the
  // portal's mention-aware renderer — we don't leak those to visitors. For
  // recognized interaction types we substitute a localized neutral string;
  // for everything else we render the raw message text as-is.
  const displayParts = $derived.by(() => {
    if (message.type === 'human-interaction-start') {
      return [{ type: 'content', text: t('chat.humanAgentJoined') }];
    }
    return renderParts(message.message);
  });
</script>

<!-- Kept on one line: the surrounding <p> is `whitespace-pre-wrap`, so any
     newline between the parts would show up as real whitespace. -->
{#snippet richText(parts: TextPart[], onDarkBubble: boolean)}{#each parts as part}{#if part.type === 'link'}<a href={hrefFor(part.text)} target="_blank" rel="noopener noreferrer" class="tashimoto-support-underline tashimoto-support-break-all {onDarkBubble ? 'tashimoto-support-text-white' : 'tashimoto-support-text-blue-600'}">{part.text}</a>{:else}{part.text}{/if}{/each}{/snippet}

{#if isSystemMessage}
  <!-- System messages use text-align centering on an inline-block bubble.
       More robust than flex+justify-center, which was confused by the
       shrink-to-fit inner wrapper. -->
  <div
    class="tashimoto-support-w-full tashimoto-support-my-1 tashimoto-support-text-center"
    data-testid="chat-message"
    data-source={message.source}
  >
    <div
      class="tashimoto-support-inline-block tashimoto-support-max-w-[85%] tashimoto-support-p-3 tashimoto-support-rounded-lg tashimoto-support-mx-auto {getBubbleClasses()}"
    >
      <p class="tashimoto-support-text-sm tashimoto-support-whitespace-pre-wrap tashimoto-support-break-words tashimoto-support-font-medium">{@render richText(displayParts, false)}</p>
      {#if message.created_at}
        <p class="tashimoto-support-text-xs tashimoto-support-mt-1 tashimoto-support-text-gray-500">
          {new Date(message.created_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      {/if}
    </div>
  </div>
{:else}
  <div
    class="tashimoto-support-flex tashimoto-support-w-full tashimoto-support-my-1 {isSelfMessage
      ? 'tashimoto-support-justify-end'
      : 'tashimoto-support-justify-start'}"
    data-testid="chat-message"
    data-greeting={greeting ? '1' : undefined}
    data-source={message.source}
  >
    <div
      class="tashimoto-support-flex tashimoto-support-items-end tashimoto-support-gap-2 {isSelfMessage
        ? 'tashimoto-support-flex-row-reverse'
        : ''}"
    >
      <div
        class="tashimoto-support-flex-shrink-0"
        style="width: 28px; height: 28px;"
      >
        {#if isAiMessage}
          <div
            class="tashimoto-support-w-[28px] tashimoto-support-h-[28px] tashimoto-support-rounded-full tashimoto-support-bg-purple-100 tashimoto-support-flex tashimoto-support-items-center tashimoto-support-justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="tashimoto-support-text-purple-600"
              aria-hidden="true"
            >
              <rect x="3" y="11" width="18" height="10" rx="2"></rect>
              <circle cx="12" cy="5" r="2"></circle>
              <path d="M12 7v4"></path>
              <line x1="8" y1="16" x2="8" y2="16"></line>
              <line x1="16" y1="16" x2="16" y2="16"></line>
            </svg>
          </div>
        {:else}
          <Avatar
            alt={senderName}
            size="tashimoto-support-w-[28px] tashimoto-support-h-[28px]"
          />
        {/if}
      </div>

      <div
        class="tashimoto-support-max-w-[75%] tashimoto-support-mx-1 tashimoto-support-p-3 tashimoto-support-rounded-lg {getBubbleClasses()}"
      >
        {#if isAiMessage}
          <div
            class="tashimoto-support-flex tashimoto-support-items-center tashimoto-support-gap-1 tashimoto-support-mb-1"
          >
            <span
              class="tashimoto-support-text-xs tashimoto-support-font-semibold tashimoto-support-text-purple-600"
            >
              {t('chat.aiAgent')}
            </span>
          </div>
        {/if}

        {#if isFileMessage && message.file_info}
          {#if isImageFile && message.file_info.url}
            <a
              href={message.file_info.url}
              target="_blank"
              rel="noopener noreferrer"
              class="tashimoto-support-block tashimoto-support-mb-1"
            >
              <img
                src={message.file_info.url}
                alt={message.file_info.name}
                class="tashimoto-support-max-w-full tashimoto-support-max-h-48 tashimoto-support-rounded"
                loading="lazy"
              />
            </a>
          {:else}
            <a
              href={message.file_info.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              download={message.file_info.name}
              class="tashimoto-support-flex tashimoto-support-items-center tashimoto-support-gap-2 tashimoto-support-text-sm tashimoto-support-underline {isSelfMessage ? 'tashimoto-support-text-white' : 'tashimoto-support-text-blue-600'}"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
              <span class="tashimoto-support-truncate">{message.file_info.name}</span>
            </a>
          {/if}
          {#if message.file_info.size}
            <p class="tashimoto-support-text-xs {isSelfMessage ? 'tashimoto-support-text-gray-200' : 'tashimoto-support-text-gray-500'}">
              {humanSize(message.file_info.size)}
            </p>
          {/if}
        {:else}
          <p class="tashimoto-support-text-sm tashimoto-support-whitespace-pre-wrap tashimoto-support-break-words">{@render richText(displayParts, isSelfMessage)}</p>
        {/if}

        {#if !greeting && message.created_at}
          <p
            class="tashimoto-support-text-xs tashimoto-support-mt-1 {isSelfMessage
              ? 'tashimoto-support-text-gray-200'
              : 'tashimoto-support-text-gray-500'}"
          >
            {new Date(message.created_at).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        {/if}
      </div>
    </div>
  </div>
{/if}
